// src/neetjee_guru/components/academicPlanner/DetailDrawer.js
import React, { Component } from "react";

class DetailDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      editedTitle: "",
      editedDescription: "",
      editedDayType: "Regular",
      editedSubType: "",
      editedTopics: {},
      editedProgress: {}
    };
  }

  componentDidMount() {
    this.syncStateFromProps(this.props);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.day !== this.props.day || prevProps.isOpen !== this.props.isOpen) {
      this.syncStateFromProps(this.props);
    }
  }

  syncStateFromProps = (props) => {
    if (props.day) {
      this.setState({
        isEditMode: false,
        editedTitle: props.day.title || "",
        editedDescription: props.day.description || "",
        editedDayType: props.day.type || "Regular",
        editedSubType: props.day.subType || "",
        editedTopics: props.day.topics ? { ...props.day.topics } : {},
        editedProgress: props.day.teachingStatus ? { ...props.day.teachingStatus } : {}
      });
    }
  };

  toggleEditMode = () => {
    this.setState(prevState => ({ isEditMode: !prevState.isEditMode }));
  };

  handleTopicTextChange = (subject, value) => {
    this.setState(prevState => ({
      editedTopics: {
        ...prevState.editedTopics,
        [subject]: value
      }
    }));
  };

  handleProgressToggle = (subject, currentStatus) => {
    const statusOrder = ["PENDING", "IN_PROGRESS", "COMPLETED", "LAGGING"];
    const nextIndex = (statusOrder.indexOf(currentStatus || "PENDING") + 1) % statusOrder.length;
    const nextStatus = statusOrder[nextIndex];

    this.setState(prevState => {
      const nextProgress = {
        ...prevState.editedProgress,
        [subject]: nextStatus
      };

      if (this.props.onUpdateStatus) {
        this.props.onUpdateStatus(this.props.day.date, subject, nextStatus);
      }

      return { editedProgress: nextProgress };
    });
  };

  handleSave = () => {
    const { onSaveDay, day } = this.props;
    const { editedTitle, editedDescription, editedDayType, editedSubType, editedTopics, editedProgress } = this.state;

    const updatedDay = {
      ...day,
      title: editedTitle,
      description: editedDescription,
      type: editedDayType,
      subType: editedSubType,
      topics: editedTopics,
      teachingStatus: editedProgress
    };

    if (onSaveDay) {
      onSaveDay(updatedDay);
    }

    this.setState({ isEditMode: false });
  };

  render() {
    const { isOpen, day, onClose, streamName, scopeMode, selectedBranch, selectedSection, userRole } = this.props;
    if (!isOpen || !day) return null;

    const { isEditMode, editedTitle, editedDescription, editedDayType, editedSubType, editedTopics, editedProgress } = this.state;
    const isBiPC = streamName === "NEET" || streamName === "Senior Inter (BiPC)";

    // Format header date
    const formattedDate = new Date(day.date).toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    });

    const isTeacher = userRole === "Teacher";

    return (
      <div className="detail-drawer-portal">
        {/* Overlay Backdrop */}
        <div className="drawer-backdrop" onClick={onClose} />

        {/* Slide-out Panel */}
        <div className="drawer-panel ds-surface">
          {/* Drawer Header */}
          <div className="drawer-header p-4 d-flex justify-content-between align-items-start border-bottom bg-light">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="small text-muted font-weight-bold text-uppercase tracking-wide">
                  {streamName}
                </span>
                <span className={`badge ${scopeMode === "Institute" ? "badge-primary" : "badge-info"}`} style={{ fontSize: "10px" }}>
                  <i className={scopeMode === "Institute" ? "fal fa-university mr-1" : "fal fa-building mr-1"} />
                  {scopeMode === "Institute" ? "Master Institute Scope" : `Branch: ${selectedBranch}`}
                </span>
                <span className="badge badge-secondary" style={{ fontSize: "10px" }}>
                  Sec: {selectedSection}
                </span>
              </div>
              <h5 className="font-weight-bold text-dark m-0">{formattedDate}</h5>
              
              <div className="mt-2 d-flex align-items-center gap-2">
                <span className={`ds-pill ${day.type === "Exam" ? "ds-pill--info" : day.type === "Holiday" ? "ds-pill--crit" : "ds-pill--mute"}`} style={{ fontSize: "11px" }}>
                  <span className="ds-dot" style={{ backgroundColor: day.type === "Exam" ? "var(--ds-info)" : day.type === "Holiday" ? "var(--ds-crit)" : "var(--ds-ink-400)" }} />
                  {day.subType || day.type} Day
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              {!isTeacher && (
                <button
                  type="button"
                  onClick={this.toggleEditMode}
                  className={`ds-btn ${isEditMode ? "ds-btn--secondary" : "ds-btn--primary"} ds-btn--sm`}
                >
                  <i className={isEditMode ? "fal fa-eye mr-1" : "fal fa-edit mr-1"} />
                  {isEditMode ? "View Mode" : "Edit Day"}
                </button>
              )}

              <button type="button" onClick={onClose} className="drawer-close-btn ds-btn ds-btn--ghost ds-btn--sm">
                <i className="fal fa-times" style={{ fontSize: "18px" }} />
              </button>
            </div>
          </div>

          {/* Drawer Content */}
          <div className="drawer-body p-4 overflow-y-auto" style={{ height: "calc(100vh - 120px)", overflowY: "auto" }}>
            
            {/* EDIT FORM MODE */}
            {isEditMode ? (
              <div className="edit-form-wrapper p-3 bg-white border rounded-lg shadow-sm">
                <h6 className="font-weight-bold text-primary mb-3">
                  <i className="fal fa-pencil-alt mr-2" /> Modify Day Schedule & Topics
                </h6>

                <div className="form-group mb-3">
                  <label className="small font-weight-bold text-muted">Day Classification Type</label>
                  <select
                    className="form-control form-control-sm"
                    value={editedDayType}
                    onChange={(e) => this.setState({ editedDayType: e.target.value })}
                  >
                    <option value="Regular">Regular Lesson</option>
                    <option value="Exam">Exam Day</option>
                    <option value="Holiday">Holiday</option>
                  </select>
                </div>

                <div className="form-group mb-3">
                  <label className="small font-weight-bold text-muted">Sub-Type / Sub-Label</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="e.g. Weekly Test 01, Festival Holiday"
                    value={editedSubType}
                    onChange={(e) => this.setState({ editedSubType: e.target.value })}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="small font-weight-bold text-muted">Day Title / Event Heading</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Event title..."
                    value={editedTitle}
                    onChange={(e) => this.setState({ editedTitle: e.target.value })}
                  />
                </div>

                <div className="form-group mb-3">
                  <label className="small font-weight-bold text-muted">Description / Remarks</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="2"
                    value={editedDescription}
                    onChange={(e) => this.setState({ editedDescription: e.target.value })}
                  />
                </div>

                <h6 className="font-weight-bold text-dark mt-4 mb-2">
                  <i className="fal fa-books mr-2 text-primary" />
                  Subject Topics (Syllabus)
                </h6>
                {Object.keys(editedTopics).length === 0 && (
                  <p className="text-muted small">No subjects mapped for this day.</p>
                )}
                {Object.keys(editedTopics).map(subject => {
                  const s = subject.trim().toLowerCase();
                  let borderColor = "#64748b";
                  if (s.includes("math"))        borderColor = "#8b5cf6";
                  else if (s.includes("physic")) borderColor = "#3b82f6";
                  else if (s.includes("chem"))   borderColor = "#10b981";
                  else if (s.includes("botan"))  borderColor = "#059669";
                  else if (s.includes("zoolog")) borderColor = "#f59e0b";

                  return (
                    <div key={subject} className="form-group mb-3 p-2 border rounded"
                      style={{ borderLeft: `4px solid ${borderColor}`, background: "#fafafa" }}>
                      <label className="small font-weight-bold text-dark d-block mb-1"
                        style={{ color: borderColor }}>
                        {subject}
                      </label>
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        placeholder={`Enter ${subject} topic for this day…`}
                        value={editedTopics[subject] || ""}
                        onChange={(e) => this.handleTopicTextChange(subject, e.target.value)}
                      />
                    </div>
                  );
                })}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.toggleEditMode}>
                    Cancel
                  </button>
                  <button type="button" className="btn btn-sm btn-success" onClick={this.handleSave}>
                    <i className="fal fa-check mr-1" /> Save Schedule Changes
                  </button>
                </div>
              </div>
            ) : (
              /* VIEW MODE */
              <div className="mb-4">
                <h6 className="section-title text-muted text-uppercase small font-weight-bold mb-3">
                  <i className="fal fa-book-open mr-2" /> Daily Syllabus Block
                </h6>
                
                {day.type !== "Regular" ? (
                  <div className="p-3 bg-light rounded-lg border">
                    <h6 className="font-weight-bold m-0" style={{ color: day.type === "Exam" ? "var(--ds-info)" : "var(--ds-crit)" }}>
                      {day.title}
                    </h6>
                    <p className="text-muted small m-0 mt-1">{day.description}</p>
                    {day.type === "Exam" && (
                      <div className="mt-3 p-2 bg-white rounded border text-xs font-weight-bold text-info" style={{ display: "inline-flex", gap: "12px" }}>
                        <span><i className="fal fa-stopwatch mr-1" /> Duration: 3 Hours</span>
                        <span><i className="fal fa-file-alt mr-1" /> Marks: 300</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {Object.keys(day.topics || {}).length === 0 && (
                      <div className="text-center py-4 text-muted">
                        <i className="fal fa-inbox" style={{ fontSize: 28 }} />
                        <p className="small mt-2 mb-0">No subject topics uploaded for this day yet.</p>
                      </div>
                    )}
                    {Object.keys(day.topics || {}).map(subject => {
                      const topic = day.topics[subject];
                      if (!topic) return null;

                      const status = (editedProgress && editedProgress[subject]) || (day.teachingStatus && day.teachingStatus[subject]) || "PENDING";

                      // Status styling
                      const statusMeta = {
                        COMPLETED:   { bg: "#d1fae5", color: "#065f46", icon: "fal fa-check-circle" },
                        IN_PROGRESS: { bg: "#fef3c7", color: "#92400e", icon: "fal fa-spinner"      },
                        LAGGING:     { bg: "#fee2e2", color: "#991b1b", icon: "fal fa-exclamation-circle" },
                        PENDING:     { bg: "#e2e8f0", color: "#475569", icon: "fal fa-clock"        }
                      }[status] || { bg: "#e2e8f0", color: "#475569", icon: "fal fa-clock" };

                      // Subject colour — case-insensitive substring match
                      const s = subject.trim().toLowerCase();
                      let borderColor = "#64748b";
                      let bgTint     = "#f8fafc";
                      let labelColor = "#334155";
                      if (s.includes("math")) {
                        borderColor = "#8b5cf6"; bgTint = "#faf5ff"; labelColor = "#6d28d9";
                      } else if (s.includes("physic")) {
                        borderColor = "#3b82f6"; bgTint = "#eff6ff"; labelColor = "#1e40af";
                      } else if (s.includes("chem")) {
                        borderColor = "#10b981"; bgTint = "#f0fdf4"; labelColor = "#065f46";
                      } else if (s.includes("botan")) {
                        borderColor = "#059669"; bgTint = "#ecfdf5"; labelColor = "#047857";
                      } else if (s.includes("zoolog")) {
                        borderColor = "#f59e0b"; bgTint = "#fffbeb"; labelColor = "#92400e";
                      }

                      return (
                        <div
                          key={subject}
                          style={{
                            background: bgTint,
                            borderLeft: `4px solid ${borderColor}`,
                            borderRadius: 10,
                            padding: "12px 14px",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.04)"
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span style={{
                              fontSize: "11px", fontWeight: 800, textTransform: "uppercase",
                              letterSpacing: "0.5px", color: labelColor
                            }}>
                              <span style={{
                                display: "inline-block", width: 8, height: 8,
                                borderRadius: "50%", background: borderColor,
                                marginRight: 6
                              }} />
                              {subject}
                            </span>

                            {/* Clickable status toggle */}
                            <button
                              type="button"
                              onClick={() => this.handleProgressToggle(subject, status)}
                              title={`Click to cycle: PENDING → IN_PROGRESS → COMPLETED → LAGGING`}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                padding: "3px 10px", borderRadius: 20,
                                border: "none", cursor: "pointer",
                                background: statusMeta.bg, color: statusMeta.color,
                                fontSize: "11px", fontWeight: 700,
                                transition: "opacity 0.15s"
                              }}
                            >
                              <i className={`${statusMeta.icon}`} style={{ fontSize: 10 }} />
                              {status.replace("_", " ")}
                            </button>
                          </div>

                          <div style={{ fontSize: "13.5px", lineHeight: "1.55", color: "#1e293b", fontWeight: 500 }}>
                            {topic}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }
}

export default DetailDrawer;

